import React, { useState, useEffect, useRef, useCallback } from 'react';
import './styles/app.css';
import './styles/ads.css';
declare global {
  interface Window {
    [key: string]: any;
    getAdvertisingToken: any;
    google: any;
    googletag: any;
    __REACT_APP_ENV__?: {
      REACT_APP_UID_JS_SDK_NAME?: string;
      REACT_APP_UID_CLIENT_BASE_URL?: string;
      REACT_APP_UID_SECURE_SIGNALS_SDK_URL?: string;
      REACT_APP_UID_SECURE_SIGNALS_STORAGE_KEY?: string;
      REACT_APP_IDENTITY_NAME?: string;
      REACT_APP_DOCS_BASE_URL?: string;
      REACT_APP_UID_CSTG_SUBSCRIPTION_ID?: string;
      REACT_APP_UID_CSTG_SERVER_PUBLIC_KEY?: string;
    };
  }
}

// Declare global variables
declare const google: any;

// Helper function to get environment variables from runtime (Kubernetes) or build-time
function getEnvVar(key: string): string | undefined {
  // First try runtime environment (injected by server.js for Kubernetes)
  if (typeof window !== 'undefined' && window.__REACT_APP_ENV__) {
    const value = window.__REACT_APP_ENV__[key as keyof typeof window.__REACT_APP_ENV__];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  // Fallback to build-time environment variable
  return process.env[key];
}

// Environment variables (read from runtime or build-time)
const UID_JS_SDK_NAME = getEnvVar('REACT_APP_UID_JS_SDK_NAME') || '__uid2';
const UID_BASE_URL = getEnvVar('REACT_APP_UID_CLIENT_BASE_URL');
const SECURE_SIGNALS_SDK_URL = getEnvVar('REACT_APP_UID_SECURE_SIGNALS_SDK_URL') || '';
const SECURE_SIGNALS_STORAGE_KEY = getEnvVar('REACT_APP_UID_SECURE_SIGNALS_STORAGE_KEY') || '';
const IDENTITY_NAME = getEnvVar('REACT_APP_IDENTITY_NAME') || 'UID2';
const DOCS_BASE_URL = getEnvVar('REACT_APP_DOCS_BASE_URL') || 'https://unifiedid.com/docs';

const clientSideIdentityOptions = {
  subscriptionId: getEnvVar('REACT_APP_UID_CSTG_SUBSCRIPTION_ID'),
  serverPublicKey: getEnvVar('REACT_APP_UID_CSTG_SERVER_PUBLIC_KEY'),
};

const SecureSignalsApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [secureSignalsLoaded, setSecureSignalsLoaded] = useState(false);
  const [secureSignalsValue, setSecureSignalsValue] = useState('undefined');
  const [targetedAdvertisingReady, setTargetedAdvertisingReady] = useState(false);
  const [advertisingToken, setAdvertisingToken] = useState('undefined');
  const [loginRequired, setLoginRequired] = useState(true);
  const [identityState, setIdentityState] = useState('');
  const [email, setEmail] = useState('');
  const [identity, setIdentity] = useState(null);
  const [adsLoaded, setAdsLoaded] = useState(false);
  const [isOptedOut, setIsOptedOut] = useState(false);

  // useRef hook to directly access DOM elements on the page
  const videoElementRef = useRef(null);
  const adContainerRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  // Track whether user has attempted to generate a token
  const loginAttemptedRef = useRef(false);

  // Helper function to get SDK instance
  const getSDK = () => {
    const sdk = window[UID_JS_SDK_NAME];
    if (!sdk) {
      console.error(`SDK not found at window.${UID_JS_SDK_NAME}. Make sure the SDK script is loaded.`);
    }
    return sdk;
  };

  const updateElements = useCallback((status) => {
    const sdk = getSDK();
    if (!sdk) {
      return;
    }
    const token = sdk.getAdvertisingToken();
    
    // Check for opt-out: only if user attempted login, and we got identity null with no token
    const optedOut = loginAttemptedRef.current && !token && status?.identity === null;
    setIsOptedOut(optedOut);

    if (sdk.getAdvertisingToken()) {
      setTargetedAdvertisingReady(true);
    } else {
      setTargetedAdvertisingReady(false);
    }
    setAdvertisingToken(String(sdk.getAdvertisingToken()));

    if (sdk.isLoginRequired() === true) {
      setLoginRequired(true);
      setIsLoggedIn(false);
    } else {
      setLoginRequired(false);
      setIsLoggedIn(true);
    }

    setIdentityState(String(JSON.stringify(status, null, 2)));

    // allow secure signals time to load
    setTimeout(updateSecureSignals, 500);
  }, []);

  const onIdentityUpdated = useCallback(
    (eventType, payload) => {
      console.log(`${IDENTITY_NAME} Callback`, payload);
      updateElements(payload);
    },
    [updateElements]
  );

  const initializeIMA = useCallback(() => {
    console.log('initializing IMA');

    function onAdsManagerLoaded(adsManagerLoadedEvent) {
      // Instantiate the AdsManager from the adsLoader response and pass it the video element.
      let adsManager = adsManagerLoadedEvent.getAdsManager(videoElementRef.current);
      adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
      adsManager.addEventListener(
        google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
        onContentPauseRequested
      );
      adsManager.addEventListener(
        google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
        onContentResumeRequested
      );
      adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, onAdLoaded);
      adsManagerRef.current = adsManager;
    }

    //adContainerRef.current!.addEventListener('click', adContainerClick);
    adDisplayContainerRef.current = new google.ima.AdDisplayContainer(
      adContainerRef.current!,
      videoElementRef.current!
    );
    let adsLoader = new google.ima.AdsLoader(adDisplayContainerRef.current);
    adsLoader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      onAdsManagerLoaded,
      false
    );
    adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);

    // Let the AdsLoader know when the video has ended
    videoElementRef.current!.addEventListener('ended', function () {
      adsLoader.contentComplete();
    });

    let adsRequest = new google.ima.AdsRequest();
    adsRequest.adTagUrl =
      'https://pubads.g.doubleclick.net/gampad/ads?' +
      'iu=/21775744923/external/single_ad_samples&sz=640x480&' +
      'cust_params=sample_ct%3Dlinear&ciu_szs=300x250%2C728x90&' +
      'gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';

    // Specify the linear and nonlinear slot sizes. This helps the SDK to
    // select the correct creative if multiple are returned.
    adsRequest.linearAdSlotWidth = videoElementRef.current!.clientWidth;
    adsRequest.linearAdSlotHeight = videoElementRef.current!.clientHeight;
    adsRequest.nonLinearAdSlotWidth = videoElementRef.current!.clientWidth;
    adsRequest.nonLinearAdSlotHeight = videoElementRef.current!.clientHeight / 3;

    // Pass the request to the adsLoader to request ads
    adsLoader.requestAds(adsRequest);
    adsLoaderRef.current = adsLoader;
  }, []);

  const loadAds = useCallback(
    (event) => {
      // Prevent this function from running on if there are already ads loaded
      if (adsLoaded) {
        return;
      }
      setAdsLoaded(true);

      // Prevent triggering immediate playback when ads are loading
      event.preventDefault();

      console.log('loading ads');

      // Initialize the container. Must be done via a user action on mobile devices.
      videoElementRef.current!.load();
      adDisplayContainerRef.current!.initialize();

      let width = videoElementRef.current!.clientWidth;
      let height = videoElementRef.current!.clientHeight;
      try {
        adsManagerRef.current!.init(width, height, google.ima.ViewMode.NORMAL);
        adsManagerRef.current!.start();
      } catch (adError) {
        // Play the video without ads, if an error occurs
        console.log('AdsManager could not be started');
        videoElementRef.current!.play();
      }
    },
    [adsLoaded]
  );

  useEffect(() => {
    // Add callbacks for UID2/EUID JS SDK
    let sdk = getSDK();
    if (!sdk) {
      // SDK not loaded yet, wait for it
      const checkSDK = setInterval(() => {
        sdk = getSDK();
        if (sdk) {
          clearInterval(checkSDK);
          if (!sdk.callbacks) {
            sdk.callbacks = [];
          }
          sdk.callbacks.push(onIdentityUpdated);
          sdk.callbacks.push((eventType: string, payload: any) => {
            if (eventType === 'SdkLoaded') {
              sdk.init({
                baseUrl: UID_BASE_URL,
              });
            }
            if (eventType === 'InitCompleted') {
              if (sdk.isLoginRequired()) {
                sdk.setIdentity(identity);
                setIdentity(identity);
              }
            }
          });
        }
      }, 100);
      return () => clearInterval(checkSDK);
    }
    
    // SDK is available, set up callbacks
    if (!sdk.callbacks) {
      sdk.callbacks = [];
    }
    sdk.callbacks.push(onIdentityUpdated);
    sdk.callbacks.push((eventType: string, payload: any) => {
      if (eventType === 'SdkLoaded') {
        sdk.init({
          baseUrl: UID_BASE_URL,
        });
      }
      if (eventType === 'InitCompleted') {
        if (sdk.isLoginRequired()) {
          sdk.setIdentity(identity);
          setIdentity(identity);
        }
      }
    });
  }, [identity, onIdentityUpdated]);

  useEffect(() => {
    // initialize ads manager
    initializeIMA();
    // videoElementRef.current!.addEventListener('play', function (event) {
    //   loadAds(event);
    // });

    // add event listener for resize
    window.addEventListener('resize', function (event) {
      console.log('window resized');
      if (adsManagerRef.current) {
        let width = videoElementRef.current!.clientWidth;
        let height = videoElementRef.current!.clientHeight;
        adsManagerRef.current.resize(width, height, google.ima.ViewMode.NORMAL);
      }
    });
  }, [initializeIMA, loadAds]);

  function onAdError(adErrorEvent) {
    // Handle the error logging.
    console.log(adErrorEvent.getError());
    if (adsManagerRef.current) {
      adsManagerRef.current.destroy();
    }
  }

  function onContentPauseRequested() {
    videoElementRef.current!.pause();
  }

  function onContentResumeRequested() {
    videoElementRef.current!.play();
  }

  function handleAdContainerClick(event) {
    console.log('ad container clicked');
    if (videoElementRef.current!.paused) {
      videoElementRef.current!.play();
    } else {
      videoElementRef.current!.pause();
    }
  }

  function onAdLoaded(adEvent) {
    let ad = adEvent.getAd();
    if (!ad.isLinear()) {
      videoElementRef.current!.play();
    }
  }

  const loadSecureSignals = () => {
    const script2 = document.createElement('script');
    script2.src = SECURE_SIGNALS_SDK_URL;
    script2.async = true;
    script2.onload = () => {
      console.log('secure signals script loaded');
    };
    document.body.append(script2);
  };

  const handleLogin = async () => {
    window.googletag.secureSignalProviders.clearAllCache();
    loginAttemptedRef.current = true; // Mark that user attempted to generate a token

    try {
      const sdk = getSDK();
      if (!sdk) {
        console.error('SDK not available. Make sure the SDK script is loaded.');
        return;
      }
      
      // Check if crypto.subtle is available (required for SDK)
      if (typeof window !== 'undefined' && !window.crypto?.subtle) {
        console.error('crypto.subtle is not available. This requires HTTPS or a secure context.');
        alert('crypto.subtle is not available. Please access this page over HTTPS.');
        return;
      }
      
      await sdk.setIdentityFromEmail(email, clientSideIdentityOptions);
      loadSecureSignals();
    } catch (e) {
      console.error('setIdentityFromEmail failed', e);
    }
  };

  const handleLogout = () => {
    window.googletag.secureSignalProviders.clearAllCache();
    const sdk = getSDK();
    sdk.disconnect();
    loginAttemptedRef.current = false; // Reset flag
    setIsOptedOut(false);
  };

  const handleTryAnother = () => {
    window.googletag.secureSignalProviders.clearAllCache();
    const sdk = getSDK();
    sdk.disconnect();
    setEmail('');
    loginAttemptedRef.current = false; // Reset flag
    setIsOptedOut(false);
  };

  const handlePlay = () => {
    // Handle play button functionality for video
    videoElementRef.current!.play();
  };

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
  };

  const updateSecureSignals = () => {
    const secureSignalsStorage = localStorage[SECURE_SIGNALS_STORAGE_KEY];
    const secureSignalsStorageJson = secureSignalsStorage && JSON.parse(secureSignalsStorage);
    if (secureSignalsStorageJson && secureSignalsStorageJson[1]) {
      setSecureSignalsLoaded(true);
      setSecureSignalsValue(JSON.stringify(secureSignalsStorageJson, null, 2));
    } else {
      setSecureSignalsLoaded(false);
      setSecureSignalsValue('undefined');
    }
  };

  return (
    <div className="page-wrapper">
      {/* Main Content Area */}
      <div className="main-content">
        <h1>React Client-Side {IDENTITY_NAME} Integration with Google Secure Signals</h1>
        <p className="intro">
          This example demonstrates how a content publisher can integrate {IDENTITY_NAME} with <strong>Google Secure Signals</strong> for Google Ad Manager, using client-side token generation. For documentation, see the{' '}
          <a href={`${DOCS_BASE_URL}/guides/integration-javascript-client-side`}>Client-Side Integration Guide for JavaScript</a> and{' '}
          <a href={`${DOCS_BASE_URL}/guides/integration-google-ss`}>Google Ad Manager Secure Signals Integration Guide</a>.
        </p>

        {/* Generate/Clear buttons at the top for easy access */}
        {isOptedOut ? (
          <>
            <div id="optout_banner" className="optout-banner">
              <p>The email address you entered has opted out of {IDENTITY_NAME}.</p>
            </div>
            <div id="optout_form" className="form top-form">
              <button type="button" className="button" onClick={handleTryAnother}>
                Try Another Email
              </button>
            </div>
          </>
        ) : !isLoggedIn ? (
          <div id="login_form" className="form top-form">
            <div className="email_prompt">
              <input
                type="text"
                id="email"
                name="email"
                placeholder="Enter an email address"
                value={email}
                onChange={handleEmailChange}
              />
              <button type="button" className="button" onClick={handleLogin}>
                Generate {IDENTITY_NAME}
              </button>
            </div>
          </div>
        ) : (
          <div id="logout_form" className="form top-form">
            <button type="button" className="button" onClick={handleLogout}>
              Clear {IDENTITY_NAME}
            </button>
          </div>
        )}

        {/* UID2 Integration Status Section */}
        <h2>{IDENTITY_NAME} Integration Status</h2>
        <p className="section-summary"><strong>Note:</strong> This is a test-only integration environment—it does not collect data or generate production-level tokens.</p>

        <table id="uid_state">
          <tbody>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Ready for Targeted Advertising:
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      Indicates whether a valid {IDENTITY_NAME} token is present and can be used for personalized ad targeting.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{targetedAdvertisingReady ? 'yes' : 'no'}</pre></td>
            </tr>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Advertising Token:
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      The encrypted {IDENTITY_NAME} token passed to Google Ad Manager for advertising. It is automatically refreshed by the SDK in the background when expired.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{advertisingToken}</pre></td>
            </tr>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Is Login Required?
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      Indicates whether a new {IDENTITY_NAME} token needs to be generated. Returns "yes" when no valid identity exists or the current identity has expired.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{loginRequired ? 'yes' : 'no'}</pre></td>
            </tr>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Has Opted Out?
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      Shows whether the user has exercised opt-out, in which case no advertising token may be generated or used.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{isOptedOut ? 'yes' : 'no'}</pre></td>
            </tr>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Identity Callback State:
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      The complete identity object returned by the SDK. Contains the full {IDENTITY_NAME} identity data including refresh tokens and metadata.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{identityState}</pre></td>
            </tr>
          </tbody>
        </table>

        {/* Google Secure Signals Section */}
        <h2 className="section-teal">Google Secure Signals Status</h2>
        <p className="section-summary">Secure Signals automatically reads the token from storage and shares it with Google Ad Manager.</p>

        <table id="secure_signals_state">
          <tbody>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Secure Signals Loaded?
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      Indicates whether Google Secure Signals has successfully loaded and stored the {IDENTITY_NAME} token. Returns "yes" when signals are available in localStorage.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{secureSignalsLoaded ? 'yes' : 'no'}</pre></td>
            </tr>
            <tr>
              <td className="label">
                <div className="tooltip-wrapper">
                  Secure Signals Value:
                  <div className="tooltip">
                    <span className="tooltip-trigger">?</span>
                    <div className="tooltip-content">
                      The encrypted {IDENTITY_NAME} signals stored by Google in localStorage under the key '{SECURE_SIGNALS_STORAGE_KEY}'. These signals are shared with Google Ad Manager.
                    </div>
                  </div>
                </div>
              </td>
              <td className="value"><pre>{secureSignalsValue}</pre></td>
            </tr>
          </tbody>
        </table>

        {/* Video Ad Container - at the bottom */}
        <div id="page-content">
          <div id="video-container">
            <video id="video-element" ref={videoElementRef} onClick={handlePlay}>
              <source src="https://storage.googleapis.com/interactive-media-ads/media/android.mp4" />
              <source src="https://storage.googleapis.com/interactive-media-ads/media/android.webm" />
            </video>
            <div id="ad-container" ref={adContainerRef} onClick={handleAdContainerClick}></div>
          </div>
          <button id="play-button" onClick={handlePlay}>
            Play
          </button>
        </div>
      </div>

      {/* Sidebar for Instructions */}
      <aside className="sidebar">
        <h3>📋 How to Test</h3>
        
        <div className="section">
          <h4>Step 1: Generate {IDENTITY_NAME}</h4>
          <ul>
            <li>Enter an email address in the input field</li>
            <li>Click "Generate {IDENTITY_NAME}" button</li>
            <li>Wait for the SDK to initialize the token</li>
          </ul>
        </div>

        <div className="section">
          <h4>Step 2: Observe Integration</h4>
          <ul>
            <li>Check "Integration Status" table for token values</li>
            <li>Verify Secure Signals data appears</li>
            <li>Note: Reload the page to update Secure Signals in localStorage</li>
          </ul>
        </div>

        <div className="section">
          <h4>Step 3: View Video Ad</h4>
          <ul>
            <li>Video player appears after successful token generation</li>
            <li>Click "Play" to watch the ad</li>
            <li>Ad request includes {IDENTITY_NAME} token via Secure Signals</li>
          </ul>
        </div>

        <div className="section">
          <h4>Step 4: Test Opt-Out</h4>
          <ul>
            <li>Try the special email: <strong>test@example.com</strong></li>
            <li>Observe "Has opted out?" changes to "yes"</li>
            <li>No advertising token is generated</li>
          </ul>
        </div>

        <div className="section">
          <h4>What's Happening?</h4>
          <ul>
            <li><strong>Client-Side Token Generation:</strong> The SDK generates tokens directly in the browser</li>
            <li><strong>Auto-Refresh:</strong> Tokens are automatically refreshed by the SDK in the background when expired</li>
            <li><strong>Local Storage:</strong> The SDK stores identity in localStorage (__uid2_advertising_token or __euid_advertising_token) for persistence across page loads</li>
            <li><strong>Google Secure Signals:</strong> Encrypted tokens are stored in localStorage under {SECURE_SIGNALS_STORAGE_KEY} and shared with Google Ad Manager</li>
            <li><strong>IMA SDK:</strong> Displays video ads with {IDENTITY_NAME} targeting</li>
          </ul>
        </div>

        <div className="note">
          <strong>Note:</strong> This is a test-only environment. Do not use real user data.
        </div>
      </aside>
    </div>
  );
};

export default SecureSignalsApp;
